import Paystack from "@paystack/inline-js";
import classNames from "classnames";
import { FormikErrors, FormikTouched, useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { BackButton } from "../../../components/buttons/backButton";
import { Button } from "../../../components/buttons/button";
import { InfoCard } from "../../../components/cards/infoCard";
import { ProfileIcon } from "../../../components/cards/profilePic";
import { Icon } from "../../../components/icon/icon";
import { Checkbox } from "../../../components/inputs/checkbox";
import { CountdownTimer } from "../../../components/inputs/countdownTimer";
import { PhoneInputSet } from "../../../components/inputs/phoneInputSet";
import { SwitchInput } from "../../../components/inputs/switch";
import { TextInput } from "../../../components/inputs/textInput";
import { TicketReservationExpiredModal } from "../../../components/modal/app_modals/ticketReservationExpiredModal";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { getDebouncedCall } from "../../../customeHooks/useDebounce";
import { useModalControl } from "../../../customeHooks/useModalControl";
import { AuthProfile } from "../../../redux/features/auth/auth.types";
import {
  useBuyTicktesMutation,
  useSubscribeToMailingListMutation,
  useUpdateHeldTicketsMutation,
  useValidateDiscountMutation,
} from "../../../redux/features/event/event.api";
import {
  BuyTicketsPayload,
  CreatedEvent,
  CreatedTicket,
  EventGuest,
} from "../../../redux/features/event/event.types";
import {
  PayoutCurrency,
  PaystackSuccessData,
} from "../../../redux/features/payment/payment.types";
import { useAppSelector } from "../../../redux/hooks";
import {
  clientAuthTokenCookieKey,
  clientProfileCookieKey,
  currencyShorts,
  PRODUCT_PRICING,
  TICKET_RESERVATION_TIME,
} from "../../../utils/constants";
import {
  calcFees,
  calcTotalTicketPrice,
  formatToCurrency,
  getCookie,
  getEventCurrencyFromTickets,
  parsePhoneNumber,
} from "../../../utils/helpers";
import { BaseApiErrorResponse, TicketPurchase } from "../../../utils/types";
import { PurchaseTicketSchema } from "../../../utils/validations";
import { ChooseTickets } from "./components/chooseTickets";

const payStackPopup = new Paystack();

type SingleGuest = {
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  ticketId: string;
};

const handleDebouncedCall = getDebouncedCall(3000);

export const PurchaseTickets = ({
  event,
  setStep,
  setGuestDetails,
  tickets,
  ticketsPurchase,
  setTicketsPurchase,
  individualTicketsPurchase,
  doHoldTicket = false,
  sessionId,
  useCookieProfile = true,
  refetchTickets,
  isPurchasingOnlyFree,
}: {
  event: CreatedEvent;
  tickets: CreatedTicket[];
  ticketsPurchase: TicketPurchase[];
  setTicketsPurchase: (data: TicketPurchase[]) => void;
  setStep: (data: 1 | 2 | "success") => void;
  setGuestDetails: (data: EventGuest | undefined) => void;
  individualTicketsPurchase: CreatedTicket[];
  doHoldTicket?: boolean;
  sessionId?: string;
  useCookieProfile?: boolean;
  refetchTickets: () => void;
  isPurchasingOnlyFree: boolean;
}) => {
  const [editingTickets, setEditingTickets] = useState(false);
  const [total, setTotal] = useState<number>(0);
  const [purchaserIndex, setPurchaserIndex] = useState<number | undefined>();
  const [totalTicketQuantity, setTotalTicketQuantity] = useState(0);
  const [totalPaidTicketQuantity, setTotalPaidTicketQuantity] = useState(0);
  const [
    totalPaidTicketQuantityForCharge,
    setTotalPaidTicketQuantityForCharge,
  ] = useState(0);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [isSignedIn, setIsSignedIn] = useState<boolean>(false);
  const [eventCurrency, setEventCurrency] = useState<PayoutCurrency>("NGN");

  const { profile: stateProfile, isSignedIn: stateIsSignedIn } = useAppSelector(
    (state) => state.auth,
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();

  const [buyTickets, { isLoading: buyingTickets }] = useBuyTicktesMutation();

  const [updateHeldTickets] = useUpdateHeldTicketsMutation();

  const [subscribeToMailinngList, { isLoading: subscribingToMailingList }] =
    useSubscribeToMailingListMutation();

  const [
    validateDiscount,
    {
      data: discount,
      isLoading: fetchingDiscount,
      error: discountError,
      isError: isDiscountError,
      reset: resetDiscount,
    },
  ] = useValidateDiscountMutation();

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleSubmit,
    setFieldValue,
    handleChange,
    validateForm,
  } = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      hasGuests: false,
      guests: [],
      hasDiscountCode: false,
      discountCode: "",
      subscribeToMailing: false,
    } as {
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      hasGuests: boolean;
      guests: SingleGuest[];
      hasDiscountCode: boolean;
      discountCode: string;
      subscribeToMailing: boolean;
    },
    enableReinitialize: false,
    validationSchema: PurchaseTicketSchema,
    onSubmit: async (vals) => {
      if (purchaserIndex === undefined && values.hasGuests) {
        toast.error("Assign a ticket to yourself");
        return;
      }

      const payload: BuyTicketsPayload = {
        mainTicketId:
          values.hasGuests && purchaserIndex !== undefined
            ? values.guests[purchaserIndex].ticketId
            : undefined,
        amount: calcTotalTicketPrice(
          total,
          eventCurrency,
          discount?.data?.value,
          discount?.data?.type,
          // event.feePayer === "guest",
          false,
          totalPaidTicketQuantity,
        ),
        fees: calcFees(
          total,
          eventCurrency,
          discount?.data?.value,
          discount?.data?.type,
          totalPaidTicketQuantityForCharge,
        ),
        currency: eventCurrency,
        email: vals.email,
        phoneNumber: vals.phoneNumber.replaceAll(" ", ""),
        callbackUrl:
          eventCurrency === "NGN"
            ? `${window.location.origin}/${encodeURIComponent(event.slug)}`
            : `${window.location.origin}/${encodeURIComponent(event.slug)}/stripe${sessionId ? `/${sessionId}` : ""}`,
        firstName: vals.firstName,
        lastName: vals.lastName,
        userId: isSignedIn ? profile?._id : undefined,
        tickets: ticketsPurchase.map((tp) => {
          return {
            ticketId: tp.ticket._id,
            quantity: tp.quantity,
          };
        }),
        invitedGuests: vals.hasGuests
          ? vals.guests
              .filter((g, idx) => idx !== purchaserIndex)
              .map((g) => ({
                firstName: g.firstName,
                lastName: g.lastName,
                email: g.email,
                ticketId: g.ticketId,
              }))
          : undefined,
        discountCode:
          vals.hasDiscountCode && !!vals.discountCode && !discountError
            ? vals.discountCode
            : undefined,
        discountCreatedBy:
          vals.hasDiscountCode && !!vals.discountCode && !discountError
            ? (discount?.data.createdBy as unknown as string)
            : undefined,
      };

      // Subscribe to mailing list if user agrees to subscription
      if (vals.subscribeToMailing) {
        await subscribeToMailinngList({
          firstName: payload.firstName,
          lastName: payload.lastName,
          email: payload.email,
        });
      }

      const resp = await buyTickets({
        id: event._id,
        data: payload,
      });

      if (resp.data?.status === "success" && resp.data.data.paymentUrl) {
        if (eventCurrency === "NGN") {
          // @ts-ignore
          payStackPopup.resumeTransaction(
            resp.data.data.payment?.access_code!,
            // @ts-ignore
            {
              onSuccess: (successData: PaystackSuccessData) => {
                setGuestDetails(resp.data?.data?.guest?.guestResponse);
                setStep("success");
              },
              onCancel: () => {
                console.log("Payment canceled!");
              },
            },
          );
        } else {
          window.open(resp.data.data.paymentUrl, "_self");
        }
      } else if (
        resp.data?.status === "success" &&
        !resp.data.data.paymentUrl
      ) {
        setGuestDetails(resp.data?.data?.guest?.guestResponse);
        setStep("success");
      }
    },
  });

  useEffect(() => {
    setFieldValue(
      "guests",
      individualTicketsPurchase.map((tp, idx) => ({
        firstName: "",
        lastName: "",
        email: "",
        title: `Guest ${idx + 1} - ${tp.name}`,
        ticketId: tp._id,
      })),
    );
    setPurchaserIndex(undefined);
    // eslint-disable-next-line
  }, [individualTicketsPurchase]);

  useEffect(() => {
    if (isSignedIn) {
      setFieldValue("firstName", profile?.firstName);
      setFieldValue("lastName", profile?.lastName);
      setFieldValue("email", profile?.email);
      setFieldValue(
        "phoneNumber",
        profile?.phoneNumber
          ? (parsePhoneNumber(
              `${profile.phoneNumber}`,
            )?.formatInternational() ?? profile.phoneNumber)
          : "",
      );

      validateForm();
    }
    // eslint-disable-next-line
  }, [profile, isSignedIn]);

  useEffect(() => {
    if (useCookieProfile) {
      const cookieProfile: AuthProfile = getCookie(clientProfileCookieKey)
        ? JSON.parse(getCookie(clientProfileCookieKey)!)
        : undefined;

      setProfile(cookieProfile);
      setIsSignedIn(!!getCookie(clientAuthTokenCookieKey));
    } else {
      setProfile(stateProfile);
      setIsSignedIn(stateIsSignedIn);
    }
  }, [stateProfile, stateIsSignedIn, useCookieProfile]);

  useEffect(() => {
    // Set total ticket quantity
    if (ticketsPurchase.length > 0) {
      const t = ticketsPurchase
        .map((tp) => tp.quantity)
        .reduce((prev, curr, currIdx, arr) => prev + curr, 0);

      setTotalTicketQuantity(t);
    }

    // Set total paid ticket quantity
    if (ticketsPurchase.length > 0) {
      const t = ticketsPurchase
        .filter((tp) => tp.ticket.price > 0)
        .map((tp) => tp.quantity)
        .reduce((prev, curr, currIdx, arr) => prev + curr, 0);

      setTotalPaidTicketQuantity(t);
    }

    // Set total paid ticket quantity eligible for fixed charges
    if (ticketsPurchase.length > 0) {
      const t = ticketsPurchase
        .filter(
          (tp) =>
            tp.ticket.price >
            PRODUCT_PRICING[tp.ticket.currency ?? "NGN"]
              .minTicketPriceForFixedFeeCharge,
        )
        .map((tp) => tp.quantity)
        .reduce((prev, curr, currIdx, arr) => prev + curr, 0);

      setTotalPaidTicketQuantityForCharge(t);
    }

    // Set total ticket amount
    const t = ticketsPurchase
      .map((tp) =>
        tp.ticket.ticketType === "free" ? 0 : tp.quantity * tp.ticket.price,
      )
      .reduce((prev, curr, currIdx, arr) => prev + curr, 0);
    setTotal(t);

    // eslint-disable-next-line
  }, [ticketsPurchase]);

  useEffect(() => {
    // Debouce updating held tickets
    if (doHoldTicket && sessionId && editingTickets) {
      handleDebouncedCall(async () => {
        const resp = await updateHeldTickets({
          sessionId: sessionId,
          ticketSelections: ticketsPurchase
            .filter((tp) => tp.ticket.quantityType === "limited")
            .map((tp) => {
              return {
                eventId: event._id,
                ticketId: tp.ticket._id,
                quantity: tp.quantity,
              };
            }),
        });

        if (resp.data?.status === "success") {
          setEditingTickets(false);
          refetchTickets();
        }
      });
    }
    // eslint-disable-next-line
  }, [ticketsPurchase, editingTickets]);

  useEffect(() => {
    setEventCurrency(getEventCurrencyFromTickets(tickets) ?? "NGN");
  }, [tickets]);

  return (
    <div
      ref={containerRef}
      className="mx-auto flex w-full flex-col gap-3 xl:max-w-[1070px]"
    >
      <BackButton
        onClick={() => {
          setStep(1);
        }}
        text="Back"
      />

      <div className="flex w-full flex-col gap-3 md:flex-row md:flex-wrap md:items-start md:justify-between md:gap-10">
        <div className="relative flex w-full flex-col gap-3 xl:max-w-[600px]">
          <BgWrapper className="flex flex-col gap-4 !p-4">
            <div className="flex items-center justify-between gap-4">
              <MiniHeader header="Tickets" className="!text-grey-700" />

              {!editingTickets && (
                <button
                  type="button"
                  className="text-sm font-bold md:text-base"
                  onClick={() => {
                    setEditingTickets(true);
                  }}
                >
                  Edit
                </button>
              )}
            </div>

            <ChooseTickets
              tickets={tickets ?? []}
              ticketsPurchase={ticketsPurchase}
              setTicketsPurchase={setTicketsPurchase}
              editableView
              isEdit={editingTickets}
              minPurchaseQuantity={1}
              showControls={editingTickets}
              liveQuantityTracking={doHoldTicket}
            />
          </BgWrapper>

          <BgWrapper className="flex flex-col gap-4 !p-4">
            <MiniHeader
              header="Contact Information"
              className="!text-grey-700"
            />

            {isSignedIn ? (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-base font-bold text-primary">
                      {values.firstName} {values.lastName}
                    </p>
                    <p className="text-sm text-grey-900">{values.email}</p>
                  </div>

                  <ProfileIcon
                    dimension={40}
                    img={undefined}
                    className="!p-[10px]"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <PhoneInputSet
                    className="col-span-full"
                    value={values.phoneNumber}
                    onChange={(v) => {
                      setFieldValue("phoneNumber", v);
                    }}
                    onBlur={handleBlur}
                    errors={
                      errors.phoneNumber && touched.phoneNumber
                        ? errors.phoneNumber
                        : ""
                    }
                    name="phoneNumber"
                    disabled={isSignedIn && !!profile?.phoneNumber}
                  />

                  <p className="text-xs text-grey-700">
                    We’d send you event reminders through WhatsApp
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid w-full grid-cols-2 gap-4">
                <TextInput
                  type="text"
                  name="firstName"
                  value={values.firstName}
                  placeholder="Enter first name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title="First name"
                  errors={
                    errors.firstName && touched.firstName
                      ? errors.firstName
                      : ""
                  }
                  readOnly={isSignedIn}
                  autoFocus
                  autoFocusOnMobile={false}
                />

                <TextInput
                  type="text"
                  name="lastName"
                  value={values.lastName}
                  placeholder="Enter last name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title="Last name"
                  errors={
                    errors.lastName && touched.lastName ? errors.lastName : ""
                  }
                  readOnly={isSignedIn}
                />

                <div className="col-span-full flex flex-col gap-1">
                  <PhoneInputSet
                    className="w-full"
                    value={values.phoneNumber}
                    onChange={(v) => {
                      setFieldValue("phoneNumber", v);
                    }}
                    onBlur={handleBlur}
                    errors={
                      errors.phoneNumber && touched.phoneNumber
                        ? errors.phoneNumber
                        : ""
                    }
                    name="phoneNumber"
                  />
                  <p className="text-xs text-grey-700">
                    We’d send you event reminders through WhatsApp
                  </p>
                </div>

                <TextInput
                  type="text"
                  name="email"
                  value={values.email}
                  placeholder="Enter email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title="Email"
                  containerClassName="col-span-full"
                  errors={errors.email && touched.email ? errors.email : ""}
                  readOnly={isSignedIn}
                />
              </div>
            )}

            <Checkbox
              checkStatus={values.subscribeToMailing}
              handleChange={() => {
                setFieldValue("subscribeToMailing", !values.subscribeToMailing);
              }}
              text="Send me emails about exciting events happening soon"
              textPosition="right"
              className="col-span-full text-left"
            />

            <div
              className={classNames(
                "flex items-center justify-between border-t border-grey-100 pt-4",
                {
                  hidden: values.guests.length === 0,
                },
              )}
            >
              <p className="text-sm font-bold">Send ticket to another email</p>

              <SwitchInput
                checked={values.hasGuests}
                onChange={() => {
                  setFieldValue("hasGuests", !values.hasGuests);
                }}
                disabled={false}
                className=""
              />
            </div>
          </BgWrapper>

          {values.hasGuests && (
            <BgWrapper className="flex flex-col gap-4 !p-4">
              <MiniHeader header="Assign Tickets" className="!text-grey-700" />

              {values.guests.map((guest, idx) => {
                return (
                  <div
                    className="grid w-full grid-cols-2 gap-4 border-b border-grey-100 pb-4 last:border-b-0 last:pb-0"
                    key={`guest-${idx}`}
                  >
                    <p className="col-span-full text-sm font-bold">
                      {guest.title}
                    </p>

                    <TextInput
                      type="text"
                      name={`guests[${idx}].firstName`}
                      value={values.guests[idx].firstName}
                      placeholder="Enter guest's first name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasTitle
                      title="First name"
                      errors={
                        (errors.guests as FormikErrors<SingleGuest>[])?.length >
                          0 &&
                        (touched.guests as FormikTouched<SingleGuest>[])
                          ?.length > 0 &&
                        (errors.guests as FormikErrors<SingleGuest>[])[idx]
                          ?.firstName &&
                        (touched.guests as FormikTouched<SingleGuest>[])[idx]
                          ?.firstName &&
                        purchaserIndex !== idx
                          ? (errors.guests as FormikErrors<SingleGuest>[])[idx]
                              .firstName
                          : ""
                      }
                      readOnly={purchaserIndex === idx}
                    />

                    <TextInput
                      type="text"
                      name={`guests[${idx}].lastName`}
                      value={values.guests[idx].lastName}
                      placeholder="Enter guest's last name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasTitle
                      title="Last name"
                      errors={
                        (errors.guests as FormikErrors<SingleGuest>[])?.length >
                          0 &&
                        (touched.guests as FormikTouched<SingleGuest>[])
                          ?.length > 0 &&
                        (errors.guests as FormikErrors<SingleGuest>[])[idx]
                          ?.lastName &&
                        (touched.guests as FormikTouched<SingleGuest>[])[idx]
                          ?.lastName &&
                        purchaserIndex !== idx
                          ? (errors.guests as FormikErrors<SingleGuest>[])[idx]
                              .lastName
                          : ""
                      }
                      readOnly={purchaserIndex === idx}
                    />

                    <TextInput
                      type="text"
                      name={`guests[${idx}].email`}
                      value={values.guests[idx].email}
                      placeholder="Enter guest's email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasTitle
                      title="Email"
                      containerClassName="col-span-full"
                      errors={
                        (errors.guests as FormikErrors<SingleGuest>[])?.length >
                          0 &&
                        (touched.guests as FormikTouched<SingleGuest>[])
                          ?.length > 0 &&
                        (errors.guests as FormikErrors<SingleGuest>[])[idx]
                          ?.email &&
                        (touched.guests as FormikTouched<SingleGuest>[])[idx]
                          ?.email &&
                        purchaserIndex !== idx
                          ? (errors.guests as FormikErrors<SingleGuest>[])[idx]
                              .email
                          : ""
                      }
                      readOnly={purchaserIndex === idx}
                    />

                    <div className="col-span-full">
                      <Checkbox
                        disabled={
                          !values.firstName || !values.lastName || !values.email
                        }
                        checkStatus={purchaserIndex === idx}
                        handleChange={async (checked) => {
                          if (checked) {
                            if (purchaserIndex !== undefined) {
                              setFieldValue(
                                `guests[${purchaserIndex}].firstName`,
                                "",
                              );
                              setFieldValue(
                                `guests[${purchaserIndex}].lastName`,
                                "",
                              );
                              setFieldValue(
                                `guests[${purchaserIndex}].email`,
                                "",
                              );
                            }

                            setPurchaserIndex(idx);

                            setFieldValue(
                              `guests[${idx}].firstName`,
                              values.firstName,
                            );
                            setFieldValue(
                              `guests[${idx}].lastName`,
                              values.lastName,
                            );
                            setFieldValue(`guests[${idx}].email`, values.email);
                          } else {
                            setFieldValue(
                              `guests[${purchaserIndex}].firstName`,
                              "",
                            );
                            setFieldValue(
                              `guests[${purchaserIndex}].lastName`,
                              "",
                            );
                            setFieldValue(
                              `guests[${purchaserIndex}].email`,
                              "",
                            );

                            setPurchaserIndex(undefined);
                          }
                        }}
                        text="This is my ticket"
                      />
                    </div>
                  </div>
                );
              })}
            </BgWrapper>
          )}
        </div>

        <div className="sticky top-0 w-full xl:max-w-[400px]">
          <BgWrapper className="flex w-full flex-col gap-4 !p-4">
            <MiniHeader header="Summary" />

            <div className="flex flex-col gap-4 border-b border-grey-100 pb-4">
              {ticketsPurchase.map((tp, idx) => {
                return (
                  <div
                    key={`${tp.ticket._id}-${idx}`}
                    className="flex items-center justify-between"
                  >
                    <p className="text-grey-500">
                      {tp.quantity}x {tp.ticket.name}
                    </p>
                    <p className="font-bold">
                      {tp.ticket.ticketType === "paid"
                        ? formatToCurrency(tp.quantity * tp.ticket.price, {
                            currency: currencyShorts[eventCurrency],
                          })
                        : isPurchasingOnlyFree
                          ? "FREE"
                          : formatToCurrency(0, {
                              currency: currencyShorts[eventCurrency],
                            })}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col gap-4 border-b border-grey-100 pb-4">
              {discount?.data && (
                <div className="flex items-center justify-between">
                  <p className="text-grey-500">Discount</p>
                  <p className="font-bold">
                    -
                    {formatToCurrency(
                      discount.data.type === "amount"
                        ? discount.data.value
                        : (discount.data.value / 100) * total,
                      { currency: currencyShorts[eventCurrency] },
                    )}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <p className="text-grey-500">Fees</p>
                <p className="font-bold">
                  {formatToCurrency(
                    event.feePayer === "organiser"
                      ? 0
                      : calcFees(
                          total,
                          eventCurrency,
                          discount?.data?.value,
                          discount?.data?.type,
                          totalPaidTicketQuantityForCharge,
                        ),
                    { currency: currencyShorts[eventCurrency] },
                  )}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-grey-500">Subtotal</p>
                <p className="font-bold">
                  {isPurchasingOnlyFree
                    ? "FREE"
                    : formatToCurrency(
                        calcTotalTicketPrice(
                          total,
                          eventCurrency,
                          discount?.data?.value,
                          discount?.data?.type,
                          event.feePayer === "guest",
                          totalPaidTicketQuantityForCharge,
                        ),
                        { currency: currencyShorts[eventCurrency] },
                      )}
                </p>
              </div>
            </div>

            {total > 0 && (
              <div className="flex flex-wrap gap-4 border-b border-grey-100 pb-4">
                {!values.hasDiscountCode ? (
                  <button
                    className="flex w-fit items-center gap-1 text-base font-bold"
                    type="button"
                    onClick={() => {
                      setFieldValue("hasDiscountCode", true);
                    }}
                  >
                    <Icon svg="plus" />
                    Add discount code
                  </button>
                ) : (
                  <>
                    {discount?.status === "success" && values.discountCode ? (
                      <div className="flex w-full items-center gap-1 text-success">
                        <Icon svg="check" width={20} height={20} />
                        <MiniHeader
                          header="Discount applied"
                          className="!text-success"
                        />
                      </div>
                    ) : (
                      <MiniHeader
                        header="Add discount code"
                        className="w-full"
                      />
                    )}

                    {discount?.status === "success" && values.discountCode ? (
                      <div className="flex w-full items-start gap-3 rounded-md border border-grey-100 p-3">
                        <div className="flex flex-1 flex-col gap-[2px] text-sm">
                          <p className="font-bold text-grey-900">
                            {discount.data.name}
                          </p>
                          <p className="text-grey-500">
                            {discount.data.type === "amount"
                              ? formatToCurrency(discount.data.value, {
                                  currency: currencyShorts[eventCurrency],
                                })
                              : `${discount.data.value}%`}{" "}
                            OFF
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            resetDiscount();
                            setFieldValue("hasDiscountCode", false);
                            setFieldValue("discountCode", "");
                          }}
                          className="w-fit text-base font-bold text-danger"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <TextInput
                          type="text"
                          name="discountCode"
                          value={values.discountCode}
                          placeholder="Enter discount code"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          hasTitle
                          title="Discount Code"
                          errors={
                            isDiscountError
                              ? ((discountError as BaseApiErrorResponse)?.data
                                  ?.message ?? "An error occurred")
                              : ""
                          }
                          containerClassName="flex-1"
                        />

                        <Button
                          type="button"
                          className="!h-fit !max-w-[100px]"
                          variation="plain-grey"
                          text="Apply"
                          disabled={["", undefined, 0].includes(
                            values.discountCode.length,
                          )}
                          loading={fetchingDiscount}
                          onClick={async () => {
                            await validateDiscount({
                              code: values.discountCode.replaceAll(" ", ""),
                              userId: event.createdBy._id,
                              eventId: event._id,
                            });
                          }}
                        />
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-grey-500">Total</p>
              <p className="font-bold">
                {isPurchasingOnlyFree
                  ? "FREE"
                  : formatToCurrency(
                      calcTotalTicketPrice(
                        total,
                        eventCurrency,
                        discount?.data?.value,
                        discount?.data?.type,
                        event.feePayer === "guest",
                        totalPaidTicketQuantityForCharge,
                      ),
                      { currency: currencyShorts[eventCurrency] },
                    )}
              </p>
            </div>

            {doHoldTicket && (
              <InfoCard
                title={
                  <CountdownTimer
                    onClick={() => {}}
                    text="Your Ticket is Reserved for"
                    onTimerComplete={() => {
                      handleModalOpen("ticketReservationExpiredModal");
                    }}
                    showInSeconds={false}
                    time={TICKET_RESERVATION_TIME}
                    timeClassName="!no-underline"
                    className="!text-sm !text-primary"
                  />
                }
                text="Please complete your purchase to secure your ticket"
                leftIcon="clock"
                iconDimensions={20}
                leftClassName="text-primary"
                className="!items-start !gap-2 !bg-blue-bg !p-3"
              />
            )}

            <Button
              type="button"
              onClick={() => {
                handleSubmit();
              }}
              text={
                calcTotalTicketPrice(
                  total,
                  eventCurrency,
                  discount?.data?.value,
                  discount?.data?.type,
                  event.feePayer === "guest",
                  totalPaidTicketQuantityForCharge,
                ) === 0
                  ? totalTicketQuantity > 1
                    ? "Get Tickets"
                    : "Get Ticket"
                  : totalTicketQuantity > 1
                    ? "Buy Tickets"
                    : "Buy Ticket"
              }
              disabled={buyingTickets || subscribingToMailingList}
              loading={buyingTickets || subscribingToMailingList}
            />

            {total > 0 && (
              <div className="flex w-full items-center justify-center">
                <img
                  src={
                    eventCurrency === "NGN"
                      ? "/assets/images/paystackSecured.png"
                      : "/assets/images/stripeSecured.png"
                  }
                  alt="paystack"
                  className="block w-full max-w-[150px] object-contain"
                />
              </div>
            )}

            <p className="text-center text-sm text-grey-500">
              By clicking{" "}
              {total > 0
                ? totalTicketQuantity > 1
                  ? `“Buy Tickets”`
                  : `“Buy Ticket”`
                : totalTicketQuantity > 1
                  ? `"Get Tickets”`
                  : `"Get Ticket”`}{" "}
              you agree to our
              <br />
              <Link to={"/legal/terms-of-use"} className="underline">
                Term of Use
              </Link>
              ,{" "}
              <Link to={"/legal/refund-policy"} className="underline">
                Refund Policy
              </Link>{" "}
              and{" "}
              <Link to={"/legal/privacy-policy"} className="underline">
                Privacy Policy
              </Link>
            </p>
          </BgWrapper>
        </div>
      </div>

      <TicketReservationExpiredModal
        open={modalOpen && modal === "ticketReservationExpiredModal"}
        handleClose={() => {
          handleModalClose();
          setTicketsPurchase([]);
          setStep(1);
        }}
      />
    </div>
  );
};
